/**
 * Event Log Page - Timeline of events
 */
import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Search,
  Filter,
  User,
  FileText,
  MessageSquare,
  Activity,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

const EventLog: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState<'all' | 'lead' | 'opportunity' | 'user'>('all');
  const [eventFilter, setEventFilter] = useState<'all' | 'created' | 'updated' | 'status_changed'>('all');

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'created':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'updated':
        return <Info className="w-5 h-5 text-blue-600" />;
      case 'status_changed':
        return <Activity className="w-5 h-5 text-purple-600" />;
      case 'message_sent':
      case 'message_received':
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case 'activity_completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'lead':
        return <User className="w-5 h-5 text-blue-600" />;
      case 'opportunity':
        return <FileText className="w-5 h-5 text-green-600" />;
      case 'user':
        return <User className="w-5 h-5 text-purple-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  const getEventLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      created: 'Criado',
      updated: 'Atualizado',
      deleted: 'Excluído',
      moved: 'Movido',
      status_changed: 'Status Alterado',
      assigned: 'Atribuído',
      commented: 'Comentado',
      message_sent: 'Mensagem Enviada',
      message_received: 'Mensagem Recebida',
      activity_completed: 'Atividade Concluída',
    };
    return labels[eventType] || eventType;
  };

  const getEntityLabel = (entityType: string) => {
    const labels: Record<string, string> = {
      lead: 'Lead',
      opportunity: 'Oportunidade',
      pipeline: 'Pipeline',
      stage: 'Etapa',
      user: 'Usuário',
      activity: 'Atividade',
      conversation: 'Conversa',
    };
    return labels[entityType] || entityType;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--glass-border)',
          borderTopColor: 'var(--color-primary-600)',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
          Timeline de Eventos
        </h1>
        <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-secondary)' }}>
          Histórico completo de ações e mudanças no sistema
        </p>
      </div>

      {/* Toolbar */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-2xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-6)',
        boxShadow: 'var(--shadow-glass)'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', width: '20px', height: '20px' }} />
            <input
              type="text"
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: 'var(--space-4)',
                paddingTop: 'var(--space-3)',
                paddingBottom: 'var(--space-3)',
                background: 'var(--glass-bg)',
                backdropFilter: 'var(--glass-blur)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-lg)',
                outline: 'none',
                fontSize: 'var(--text-sm)'
              }}
            />
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Filter style={{ width: '20px', height: '20px', color: 'var(--text-muted)' }} />
              <select
                value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value as any)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-lg)',
                  outline: 'none',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)'
                }}
              >
                <option value="all">Todas Entidades</option>
                <option value="lead">Leads</option>
                <option value="opportunity">Oportunidades</option>
                <option value="user">Usuários</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Activity style={{ width: '20px', height: '20px', color: 'var(--text-muted)' }} />
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value as any)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-lg)',
                  outline: 'none',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)'
                }}
              >
                <option value="all">Todos Eventos</option>
                <option value="created">Criados</option>
                <option value="updated">Atualizados</option>
                <option value="status_changed">Mudanças de Status</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-2xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-glass)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          {/* Today */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
              <Calendar style={{ width: '20px', height: '20px', color: 'var(--text-muted)' }} />
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--text-primary)' }}>Hoje</h3>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>17 de Janeiro de 2026</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', paddingLeft: 'var(--space-8)', borderLeft: '2px solid var(--glass-border)' }}>
              {/* Event 1 */}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-44px', padding: 'var(--space-2)', background: 'var(--color-primary-50)', borderRadius: '50%' }}>
                  {getEventIcon('created')}
                </div>
                <div style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-4)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {getEntityIcon('opportunity')}
                      <div>
                        <h4 style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          Nova Oportunidade Criada
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                          João Silva criou uma nova oportunidade
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock style={{ width: '16px', height: '16px' }} />
                        14:30
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event 2 */}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-44px', padding: 'var(--space-2)', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '50%' }}>
                  {getEventIcon('status_changed')}
                </div>
                <div style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-4)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {getEntityIcon('lead')}
                      <div>
                        <h4 style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          Status do Lead Alterado
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                          Maria Santos moveu o lead de "Novo" para "Em Negociação"
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock style={{ width: '16px', height: '16px' }} />
                        13:45
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event 3 */}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-44px', padding: 'var(--space-2)', background: 'var(--color-success-50)', borderRadius: '50%' }}>
                  {getEventIcon('activity_completed')}
                </div>
                <div style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-4)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {getEntityIcon('opportunity')}
                      <div>
                        <h4 style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          Atividade Concluída
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                          Pedro Costa marcou uma reunião como concluída
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock style={{ width: '16px', height: '16px' }} />
                        11:20
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Yesterday */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--glass-border)' }}>
              <Calendar style={{ width: '20px', height: '20px', color: 'var(--text-muted)' }} />
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--text-primary)' }}>Ontem</h3>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>16 de Janeiro de 2026</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', paddingLeft: 'var(--space-8)', borderLeft: '2px solid var(--glass-border)' }}>
              {/* Event 4 */}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-44px', padding: 'var(--space-2)', background: 'var(--color-primary-50)', borderRadius: '50%' }}>
                  {getEventIcon('updated')}
                </div>
                <div style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-4)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {getEntityIcon('user')}
                      <div>
                        <h4 style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          Usuário Atualizado
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                          Ana Oliveira atualizou seu perfil
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock style={{ width: '16px', height: '16px' }} />
                        16:50
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event 5 */}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-44px', padding: 'var(--space-2)', background: 'var(--color-primary-50)', borderRadius: '50%' }}>
                  {getEventIcon('message_sent')}
                </div>
                <div style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-4)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {getEntityIcon('lead')}
                      <div>
                        <h4 style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          Mensagem Enviada
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                          Carlos Lima enviou uma mensagem ao lead
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock style={{ width: '16px', height: '16px' }} />
                        14:15
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Load More */}
        <div style={{ marginTop: 'var(--space-6)', textAlign: 'center' }}>
          <button style={{
            padding: 'var(--space-2) var(--space-6)',
            background: 'var(--glass-bg)',
            backdropFilter: 'var(--glass-blur)',
            color: 'var(--text-primary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}>
            Carregar Mais
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventLog;
